import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IVirtualFenceSummary, VirtualFenceType } from 'app/shared/model/virtual-fence.model';

export interface VirtualFenceListState {
  loading: boolean;
  loadingList: boolean;
  errorMessage: string | null;
  entities: IVirtualFenceSummary[];
  totalItems: number;
  circleCount: number;
  polygonCount: number;
}

const initialState: VirtualFenceListState = {
  loading: false,
  loadingList: false,
  errorMessage: null,
  entities: [],
  totalItems: 0,
  circleCount: 0,
  polygonCount: 0,
};

const apiUrl = 'api/virtual-fences';

export type VirtualFenceTypeFilter = 'ALL' | 'CIRCLE' | 'POLYGON';

export interface VirtualFenceListQueryParams {
  page?: number;
  size?: number;
  sort?: string;
  companyId?: number;
  type?: VirtualFenceTypeFilter;
}

const buildRequestUrl = ({
  page,
  size,
  sort,
  companyId,
  type = 'ALL',
}: VirtualFenceListQueryParams = {}) => {
  const params = new URLSearchParams();
  if (page !== undefined) params.append('page', `${page}`);
  if (size !== undefined) params.append('size', `${size}`);
  if (sort) params.append('sort', sort);
  if (companyId !== undefined) params.append('companyId', `${companyId}`);
  params.append('type', type);
  params.append('cacheBuster', `${new Date().getTime()}`);
  return `${apiUrl}?${params.toString()}`;
};

export const getEntities = createAsyncThunk(
  'virtualFenceList/fetch_entity_list',
  async (params: VirtualFenceListQueryParams = {}) => {
    const requestUrl = buildRequestUrl(params);
    return axios.get<IVirtualFenceSummary[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const pollFences = createAsyncThunk(
  'virtualFenceList/poll_entity_list',
  async (params: VirtualFenceListQueryParams = {}) => {
    const requestUrl = buildRequestUrl(params);
    return axios.get<IVirtualFenceSummary[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const VirtualFenceListSlice = createSlice({
  name: 'virtualFenceList',
  initialState,
  reducers: {
    reset: () => initialState,
    markPendingDelete(state, action: PayloadAction<{ type: VirtualFenceType; id: number }>) {
      const { type, id } = action.payload;
      state.entities = state.entities.map(fence =>
        fence.type === type && fence.id === id
          ? { ...fence, syncStatus: 'PENDING_DELETE' as const }
          : fence
      );
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getEntities.pending, state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(getEntities.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'] ?? '0', 10);
        state.circleCount  = parseInt(action.payload.headers['x-circle-count']  ?? '0', 10);
        state.polygonCount = parseInt(action.payload.headers['x-polygon-count'] ?? '0', 10);
      })
      .addCase(getEntities.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message ?? null;
      })
      .addCase(pollFences.pending, state => {
        state.loadingList = true;
      })
      .addCase(pollFences.fulfilled, (state, action) => {
        state.loadingList = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'] ?? '0', 10);
        state.circleCount  = parseInt(action.payload.headers['x-circle-count']  ?? '0', 10);
        state.polygonCount = parseInt(action.payload.headers['x-polygon-count'] ?? '0', 10);
      })
      .addCase(pollFences.rejected, state => {
        state.loadingList = false;
      });
  },
});

export const { reset, markPendingDelete } = VirtualFenceListSlice.actions;

export default VirtualFenceListSlice.reducer;
